/**
 * ═══════════════════════════════════════════════════════════════════
 * DisclosureOS — Shared Design System + Drill-Down Infrastructure
 * ═══════════════════════════════════════════════════════════════════
 * IMPORTANT: Uses apiFetch from api_service.js to prevent "clone" errors.
 * All module files import api/post/put/patch from here.
 * ═══════════════════════════════════════════════════════════════════
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../../services/api_service";

// ═══ DESIGN SYSTEM ═══
export const C = {
  bg:"#050a14", surface:"#0c1220", card:"#111c30", cardHover:"#162040",
  border:"#1a2744", borderHi:"#2a3a5c", text:"#e2e8f0", muted:"#6b7a96", dim:"#384660",
  blue:"#3b82f6", amber:"#f59e0b", emerald:"#10b981", purple:"#8b5cf6",
  cyan:"#06b6d4", red:"#ef4444", pink:"#ec4899", teal:"#14b8a6", orange:"#f97316", white:"#fff",
};

// ═══ API HELPERS — wrapping apiFetch (handles auth, clone errors, base URL) ═══
export async function api(path, opts = {}) {
  return apiFetch(path, opts);
}
export async function post(path, body) {
  return apiFetch(path, { method: "POST", body });
}
export async function put(path, body) {
  return apiFetch(path, { method: "PUT", body });
}
export async function patch(path, body = {}) {
  return apiFetch(path, { method: "PATCH", body });
}

// ═══ BASE COMPONENTS ═══
export const Btn = ({onClick,children,color=C.blue,disabled,small}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:small?"4px 10px":"7px 16px",borderRadius:6,border:`1px solid ${color}44`,background:disabled?C.dim+"33":color+"18",color:disabled?C.dim:color,fontSize:small?10:11,fontWeight:600,cursor:disabled?"not-allowed":"pointer",transition:"all 0.2s"}}>{children}</button>
);
export const Badge = ({text,color=C.teal}) => (
  <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,background:color,color:C.bg}}>{text}</span>
);
export const Card = ({children,color=C.border,style={},onClick}) => (
  <div onClick={onClick} style={{padding:"12px 14px",borderRadius:8,background:C.card,border:`1px solid ${color}33`,marginBottom:8,cursor:onClick?"pointer":"default",transition:"all 0.15s",...style}}>{children}</div>
);
export const Label = ({children}) => <div style={{fontSize:10,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>{children}</div>;
export const Val = ({children,color=C.text}) => <div style={{fontSize:14,fontWeight:600,color}}>{children}</div>;
export const Input = ({value,onChange,placeholder,style={}}) => (
  <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{padding:"6px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%",...style}}/>
);
export const TextArea = ({value,onChange,placeholder,rows=4}) => (
  <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{padding:"8px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%",resize:"vertical",fontFamily:"inherit"}}/>
);
export const Loader = ({text="Loading..."}) => <div style={{textAlign:"center",padding:20,color:C.muted,fontSize:12}}>{text}</div>;
export const Err = ({error}) => error ? <div style={{padding:8,borderRadius:5,background:C.red+"15",border:`1px solid ${C.red}33`,color:C.red,fontSize:11,marginBottom:8}}>{error}</div> : null;
export const MetricBar = ({label,val,max=1}) => (
  <div style={{marginBottom:6}}>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:2}}>
      <span style={{color:C.muted}}>{label}</span>
      <span style={{color:val>0.7?C.emerald:val>0.5?C.amber:C.red,fontWeight:600}}>{(val*100).toFixed(1)}%</span>
    </div>
    <div style={{height:6,borderRadius:3,background:C.dim+"33"}}>
      <div style={{height:6,borderRadius:3,background:val>0.7?C.emerald:val>0.5?C.amber:C.red,width:`${(val/max)*100}%`,transition:"width 0.5s"}}/>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════
// DRILL-DOWN INFRASTRUCTURE — "No Walls" Design
// ═══════════════════════════════════════════════════════════

export const ClickVal = ({children, color=C.text, onClick}) => (
  <div onClick={onClick} style={{fontSize:14,fontWeight:600,color,cursor:onClick?"pointer":"default",
    textDecoration:onClick?"underline":"none",textDecorationColor:color+"44",
    textUnderlineOffset:2,transition:"all 0.15s"}}
    onMouseEnter={e=>{if(onClick)e.target.style.opacity="0.8"}}
    onMouseLeave={e=>{e.target.style.opacity="1"}}>{children}</div>
);

export const ClickRow = ({children, cols, onClick, style={}, active}) => (
  <div onClick={onClick} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
    padding:"7px 10px",borderBottom:`1px solid ${C.border}20`,cursor:"pointer",
    transition:"all 0.15s",borderRadius:4,background:active?C.cardHover:"transparent",...style}}
    onMouseEnter={e=>e.currentTarget.style.background=C.cardHover}
    onMouseLeave={e=>e.currentTarget.style.background=active?C.cardHover:"transparent"}>
    {cols ? cols.map((c,i) => <span key={i}>{c}</span>) : children}
  </div>
);

export const Breadcrumb = ({path=[], onNavigate}) => (
  <div style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:C.muted,marginBottom:12,flexWrap:"wrap"}}>
    {path.map((p, i) => (
      <span key={i} style={{display:"flex",alignItems:"center",gap:4}}>
        {i > 0 && <span style={{color:C.dim}}>›</span>}
        {i < path.length - 1 ? (
          <span onClick={() => (p.onClick ? p.onClick() : onNavigate && onNavigate(i))} style={{cursor:"pointer",color:C.blue,textDecoration:"underline",textDecorationColor:C.blue+"44"}}>{p.label}</span>
        ) : (
          <span style={{color:C.text,fontWeight:600}}>{p.label}</span>
        )}
      </span>
    ))}
  </div>
);

export const DetailPanel = ({title, subtitle, color=C.blue, onClose, children, actions, visible=true}) => {
  if (!visible) return null;
  return (
    <div style={{border:`1px solid ${color}44`,borderRadius:10,background:C.surface,marginBottom:12,overflow:"hidden"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",
        background:color+"12",borderBottom:`1px solid ${color}22`}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color}}>{title}</div>
          {subtitle && <div style={{fontSize:9,color:C.muted}}>{subtitle}</div>}
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          {actions}
          {onClose && <span onClick={onClose} style={{cursor:"pointer",color:C.muted,fontSize:16,lineHeight:1,padding:"2px 6px",borderRadius:4,background:C.dim+"22"}}>✕</span>}
        </div>
      </div>
      <div style={{padding:"12px 14px"}}>{children}</div>
    </div>
  );
};

export function HistoryTab({resourceId}) {
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!resourceId) return;
    api(`/v1/audit/trail/${resourceId}`).then(r => setTrail(r.entries || r.trail || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [resourceId]);
  if (loading) return <Loader text="Loading history..." />;
  if (!trail.length) return <div style={{fontSize:10,color:C.dim,padding:8}}>No history recorded</div>;
  return (
    <div>
      {trail.map((t, i) => (
        <div key={i} style={{display:"flex",gap:8,padding:"5px 0",borderBottom:`1px solid ${C.border}15`,fontSize:10}}>
          <span style={{color:C.amber,fontWeight:600,minWidth:80}}>{t.action}</span>
          <span style={{color:C.text}}>{t.actor || "system"}</span>
          <span style={{color:C.dim,marginLeft:"auto"}}>{t.timestamp ? new Date(t.timestamp).toLocaleString() : ""}</span>
        </div>
      ))}
    </div>
  );
}

export const EmptyState = ({icon, title="No data yet", message, action, onAction}) => (
  <div style={{textAlign:"center",padding:"30px 20px",border:`1px dashed ${C.border}`,borderRadius:10,margin:"12px 0"}}>
    {icon && <div style={{fontSize:28,marginBottom:8}}>{icon}</div>}
    <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4}}>{title}</div>
    {message && <div style={{fontSize:11,color:C.muted,marginBottom:12}}>{message}</div>}
    {action && <Btn onClick={onAction} color={C.blue}>{action}</Btn>}
  </div>
);

export function Tabs({tabs, active, onChange}) {
  return (
    <div style={{display:"flex",gap:2,borderBottom:`1px solid ${C.border}`,marginBottom:12}}>
      {tabs.map(t => {
        const id = typeof t === 'string' ? t : t.id;
        const label = typeof t === 'string' ? t : t.label;
        const color = typeof t === 'string' ? C.blue : (t.color || C.blue);
        return (
          <button key={id} onClick={() => onChange(id)} style={{
            padding:"6px 14px",borderRadius:"6px 6px 0 0",border:"none",
            background:active===id?C.card:"transparent",
            color:active===id?color:C.muted,
            fontSize:10,fontWeight:active===id?700:500,cursor:"pointer",
            borderBottom:active===id?`2px solid ${color}`:"2px solid transparent"
          }}>{label}</button>
        );
      })}
    </div>
  );
}

export const KV = ({label, value, color}) => (
  <div style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${C.border}10`,fontSize:10}}>
    <span style={{color:C.muted}}>{label}</span>
    <span style={{color:color||C.text,fontWeight:600}}>{value}</span>
  </div>
);

export const LinkedEntity = ({type, id, label, color=C.blue, onClick}) => (
  <span onClick={onClick} style={{
    display:"inline-flex",alignItems:"center",gap:3,padding:"1px 7px",borderRadius:4,
    fontSize:9,fontWeight:600,background:color+"15",color,border:`1px solid ${color}33`,
    cursor:"pointer",transition:"all 0.15s"}}
    onMouseEnter={e=>e.currentTarget.style.background=color+"28"}
    onMouseLeave={e=>e.currentTarget.style.background=color+"15"}>
    {label || (id ? id.slice(0, 12) : type)}
  </span>
);

// ═══ Aliases — modules import these names ═══
export const DrillRow = ClickRow;
export const TabBar = Tabs;
export const KPICard = ({label, value, color, sub, onClick}) => (
  <div onClick={onClick} style={{padding:"8px 12px",borderRadius:6,background:C.card,
    border:`1px solid ${(color||C.border)}22`,cursor:onClick?"pointer":"default",flex:1,minWidth:0}}>
    <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",letterSpacing:"0.5px"}}>{label}</div>
    <div style={{fontSize:16,fontWeight:700,color:color||C.text}}>{value}</div>
    {sub && <div style={{fontSize:8,color:C.dim,marginTop:2}}>{sub}</div>}
  </div>
);
